"use client";

import { useMemo, useState } from "react";
import { Check, Star } from "lucide-react";
import ArrowLink from "@/components/ui/ArrowLink";
import ButtonLink from "@/components/site/button-link";
import Container from "@/components/ui/Container";
import Reveal from "@/components/ui/Reveal";
import SectionHeading from "@/components/ui/SectionHeading";
import BillingPeriodToggle, {
  type BillingPeriod,
} from "@/components/ui/billing-period-toggle";
import InlineTooltip from "@/components/ui/inline-tooltip";
import MeshTag from "@/components/ui/MeshTag";
import { useRegisterUrl } from "@/lib/hooks/use-register-url";
import { useHomeContent } from "@/lib/i18n/content-context";
import { useLocale } from "@/lib/i18n/locale-context";
import { localizeHref } from "@/lib/i18n/routing";
import { cn, formatCurrency } from "@/lib/utils";

type PricingPlanFeaturePart = {
  text: string;
  tooltip?: string;
};

type PricingPlanFeature = {
  parts: PricingPlanFeaturePart[];
};

type PricingPlan = {
  name: string;
  subtitle: string;
  featuresIntro: string;
  features: PricingPlanFeature[];
  featured?: boolean;
  monthlyPrice?: number;
  annualMonthlyPrice?: number;
  priceDisplay?: string;
  priceSuffix?: string;
};

function PlanFeature({ parts }: PricingPlanFeature) {
  return (
    <li className="flex items-start gap-3 text-sm leading-5 text-foreground/85">
      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-[var(--ok)]/12">
        <Check className="size-3 text-[var(--ok)]" aria-hidden />
      </span>
      <span>
        {parts.map((part, index) =>
          part.tooltip ? (
            <InlineTooltip
              key={`${part.text}-${index}`}
              label={part.text}
              text={part.tooltip}
            />
          ) : (
            <span key={`${part.text}-${index}`}>{part.text}</span>
          ),
        )}
      </span>
    </li>
  );
}

function PricingPlanCard({
  plan,
  priceDisplay,
  priceSuffix,
  ctaHref,
  ctaLabel,
  ctaVariant = "default",
  trialIncluded,
  paygBillingNote,
  featuresLabel,
  perMonthLabel,
  popularLabel,
  compactPrice = false,
}: {
  plan: PricingPlan;
  priceDisplay: string;
  priceSuffix?: string;
  ctaHref: string;
  ctaLabel: string;
  ctaVariant?: "default" | "outline";
  trialIncluded: string;
  paygBillingNote: string;
  featuresLabel: string;
  perMonthLabel: string;
  popularLabel: string;
  compactPrice?: boolean;
}) {
  return (
    <article
      className={cn(
        "flex h-full flex-col overflow-hidden rounded-2xl border border-line bg-white",
        plan.featured && "shadow-[0_8px_30px_rgb(15_23_42/0.06)]",
      )}
    >
      <div
        className={cn(
          "px-6 pt-6 pb-6",
          plan.featured && "bg-foreground/[0.025]",
        )}
      >
        <div className="flex min-h-7 items-start justify-between gap-3">
          <h3 className="text-lg font-semibold tracking-tight text-foreground">
            {plan.name}
          </h3>
          {plan.featured ? (
            <MeshTag as="span" icon={Star} className="shrink-0">
              {popularLabel}
            </MeshTag>
          ) : (
            <span className="h-5 shrink-0" aria-hidden />
          )}
        </div>

        <div className="mt-5 flex h-10 items-end gap-2 whitespace-nowrap">
          <p
            className={cn(
              "shrink-0 font-semibold tracking-tight text-foreground",
              compactPrice
                ? "text-[2.125rem] leading-tight"
                : "text-[2.5rem] leading-none",
            )}
          >
            {priceDisplay}
          </p>
          {priceSuffix ? (
            <p className="shrink-0 pb-1 text-sm leading-tight text-muted-foreground">
              {priceSuffix}
            </p>
          ) : plan.monthlyPrice ? (
            <span
              className="shrink-0 pb-1 text-sm leading-tight opacity-0"
              aria-hidden
            >
              {perMonthLabel}
            </span>
          ) : null}
        </div>

        <p className="mt-3 h-5 overflow-hidden text-sm leading-5 text-ellipsis whitespace-nowrap text-muted-foreground">
          {plan.subtitle}
        </p>
        <p className="mt-1 h-4 overflow-hidden text-xs leading-4 text-ellipsis whitespace-nowrap text-muted-foreground">
          {plan.monthlyPrice ? trialIncluded : paygBillingNote}
        </p>

        <ButtonLink
          href={ctaHref}
          external={ctaHref.startsWith("http")}
          variant={ctaVariant}
          className="mt-6 w-full"
        >
          {ctaLabel}
        </ButtonLink>
      </div>

      <div className="mt-auto border-t border-line px-6 py-6">
        <p className="text-xs font-semibold tracking-[0.12em] text-foreground uppercase">
          {featuresLabel}
        </p>
        <p className="mt-1.5 h-5 overflow-hidden text-sm leading-5 text-ellipsis whitespace-nowrap text-muted-foreground">
          {plan.featuresIntro}
        </p>
        <ul className="mt-4 space-y-2">
          {plan.features.map((feature, index) => (
            <PlanFeature key={index} {...feature} />
          ))}
        </ul>
      </div>
    </article>
  );
}

export default function PricingSection() {
  const registerUrl = useRegisterUrl();
  const locale = useLocale();
  const { pricingContent } = useHomeContent();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
  const { standard, premium, payg } = pricingContent.plans;

  const standardPrice = useMemo(
    () =>
      billingPeriod === "monthly"
        ? standard.monthlyPrice
        : standard.annualMonthlyPrice,
    [billingPeriod, standard.annualMonthlyPrice, standard.monthlyPrice],
  );

  const premiumPrice = useMemo(
    () =>
      billingPeriod === "monthly"
        ? premium.monthlyPrice
        : premium.annualMonthlyPrice,
    [billingPeriod, premium.annualMonthlyPrice, premium.monthlyPrice],
  );

  return (
    <section className="dashboard-section -mt-px overflow-hidden border-0 py-20 md:py-28">
      <Container wide className="relative z-[1]">
        <Reveal className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 sm:items-end sm:justify-between">
          <SectionHeading
            eyebrow={pricingContent.eyebrow}
            title={pricingContent.title}
            className="w-full text-center sm:w-fit sm:text-start"
          />

          <BillingPeriodToggle
            value={billingPeriod}
            onChange={setBillingPeriod}
            monthlyLabel={pricingContent.monthlyLabel}
            annualLabel={pricingContent.annualLabel}
          />
        </Reveal>

        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          <Reveal className="h-full">
            <PricingPlanCard
              plan={standard}
              priceDisplay={formatCurrency(standardPrice)}
              priceSuffix={pricingContent.perMonthLabel}
              ctaHref={registerUrl}
              ctaLabel={pricingContent.ctaLabel}
              trialIncluded={pricingContent.trialIncluded}
              paygBillingNote={pricingContent.paygBillingNote}
              featuresLabel={pricingContent.featuresLabel}
              perMonthLabel={pricingContent.perMonthLabel}
              popularLabel={pricingContent.popularLabel}
            />
          </Reveal>

          <Reveal delay={0.05} className="h-full">
            <PricingPlanCard
              plan={premium}
              priceDisplay={formatCurrency(premiumPrice)}
              priceSuffix={pricingContent.perMonthLabel}
              ctaHref={registerUrl}
              ctaLabel={pricingContent.ctaLabel}
              trialIncluded={pricingContent.trialIncluded}
              paygBillingNote={pricingContent.paygBillingNote}
              featuresLabel={pricingContent.featuresLabel}
              perMonthLabel={pricingContent.perMonthLabel}
              popularLabel={pricingContent.popularLabel}
            />
          </Reveal>

          <Reveal delay={0.1} className="h-full">
            <PricingPlanCard
              plan={payg}
              priceDisplay={payg.priceDisplay ?? "Usage based"}
              compactPrice={locale === "ar"}
              ctaHref={pricingContent.paygHref}
              ctaLabel={pricingContent.paygCtaLabel}
              trialIncluded={pricingContent.trialIncluded}
              paygBillingNote={pricingContent.paygBillingNote}
              featuresLabel={pricingContent.featuresLabel}
              perMonthLabel={pricingContent.perMonthLabel}
              popularLabel={pricingContent.popularLabel}
            />
          </Reveal>
        </div>

        <Reveal className="mt-8">
          <ArrowLink href={localizeHref(pricingContent.compareHref, locale)}>
            {pricingContent.compareLabel}
          </ArrowLink>
        </Reveal>
      </Container>
    </section>
  );
}
